import { Search } from 'lucide-react';
import React, { useMemo } from 'react';
import type { PracticeExam } from '../../../types/practice-exam';

type ExamKind = 'TYT' | 'AYT' | 'YKS' | 'LGS';
type ExamProgress = 'open' | 'saved' | 'done';
type ExamFormat = 'online' | 'printed';

export interface StudentExamFilters {
  search: string;
  kind: ExamKind | '';
  progress: ExamProgress | '';
  grade: number | null;
  format: ExamFormat | '';
}

export const EMPTY_STUDENT_EXAM_FILTERS: StudentExamFilters = { search: '', kind: '', progress: '', grade: null, format: '' };

/** Bu sayının altında süzgeç yalnız yer kaplar. */
export const STUDENT_FILTER_MIN_EXAMS = 4;

const KIND_ORDER: ExamKind[] = ['TYT', 'AYT', 'YKS', 'LGS'];
const PROGRESS_LABELS: Array<[ExamProgress, string]> = [['open', 'Çözülmedi'], ['saved', 'Devam eden'], ['done', 'Cevaplandı']];
const FORMAT_LABELS: Array<[ExamFormat, string]> = [['online', 'Online'], ['printed', 'Basılı kitapçık']];

export const examKind = (exam: PracticeExam): ExamKind => {
  if (exam.levelType === 'lgs') return 'LGS';
  if (exam.yksMode === 'single_tyt') return 'TYT';
  return exam.yksMode === 'single_ayt' ? 'AYT' : 'YKS';
};

const examProgress = (exam: PracticeExam): ExamProgress => {
  if (exam.hasMyResult) return 'done';
  return exam.hasSavedProgress ? 'saved' : 'open';
};

/** Soruları sistemde olmayan deneme: öğrenci basılı kitapçıktan çözer, burada yalnız optik doldurur. */
export const isPrintedExam = (exam: PracticeExam) => !Object.keys(exam.questionImages || {}).length && !exam.booklets?.length;

const examFormat = (exam: PracticeExam): ExamFormat => (isPrintedExam(exam) ? 'printed' : 'online');

// Türkçe karaktersiz yazılan arama da bulsun; "İ" küçülünce birleşen nokta bıraktığı için önce o çevrilir
const fold = (value: string = '') =>
  value
    .replace(/İ/g, 'i')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const filterStudentExams = (exams: PracticeExam[], filters: StudentExamFilters) => {
  const terms = fold(filters.search).split(' ').filter(Boolean);
  return exams.filter((exam) => {
    if (filters.kind && examKind(exam) !== filters.kind) return false;
    if (filters.progress && examProgress(exam) !== filters.progress) return false;
    if (filters.grade && Number(exam.gradeLevel) !== filters.grade) return false;
    if (filters.format && examFormat(exam) !== filters.format) return false;
    const name = fold(exam.name);
    return terms.every((term) => name.includes(term));
  });
};

interface StudentExamFilterProps {
  exams: PracticeExam[];
  filters: StudentExamFilters;
  shownCount: number;
  onChange: (next: StudentExamFilters) => void;
}

/**
 * Öğrencinin Denemeler listesi süzgeci. Liste zaten bellekte olduğu için süzme burada yapılır. Sayılar diğer seçimleri hesaba katar:
 * "TYT" seçiliyken "Cevaplandı 3" gerçekten 3 kart getirir. Tek seçenekli satır gösterilmez.
 */
export default function StudentExamFilter({ exams, filters, shownCount, onChange }: StudentExamFilterProps) {
  const options = useMemo(() => {
    const present = <T,>(read: (exam: PracticeExam) => T) => new Set(exams.map(read));
    const kinds = present(examKind);
    const progresses = present(examProgress);
    const formats = present(examFormat);
    return {
      kinds: KIND_ORDER.filter((kind) => kinds.has(kind)),
      progresses: PROGRESS_LABELS.filter(([key]) => progresses.has(key)),
      formats: FORMAT_LABELS.filter(([key]) => formats.has(key)),
      grades: [...new Set(exams.map((exam) => Number(exam.gradeLevel)).filter(Boolean))].sort((a, b) => a - b),
    };
  }, [exams]);

  const active = Boolean(filters.search.trim() || filters.kind || filters.progress || filters.grade || filters.format);

  const row = <K extends 'kind' | 'progress' | 'grade' | 'format'>(
    field: K,
    title: string,
    choices: Array<[NonNullable<StudentExamFilters[K]>, string]>,
    count: (exam: PracticeExam) => StudentExamFilters[K]
  ) => {
    if (choices.length < 2) return null;
    const pool = filterStudentExams(exams, { ...filters, [field]: EMPTY_STUDENT_EXAM_FILTERS[field] });
    const chip = (value: StudentExamFilters[K], label: string) => {
      const selected = filters[field] === value;
      const total = value ? pool.filter((exam) => count(exam) === value).length : pool.length;
      return (
        <button
          key={String(value)}
          type="button"
          aria-pressed={selected}
          onClick={() => onChange({ ...filters, [field]: value })}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition-colors ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          {label}
          <span className={`text-xs ${selected ? 'text-blue-600' : 'text-gray-400'}`}>{total}</span>
        </button>
      );
    };
    return (
      // Telefonda satır alta bölünmez, yana kayar: süzgeç kartları ekranın altına itmesin
      <div className="flex items-center gap-1.5 overflow-x-auto sm:flex-wrap sm:overflow-visible">
        <span className="w-12 shrink-0 text-xs font-medium text-gray-700">{title}</span>
        {chip(EMPTY_STUDENT_EXAM_FILTERS[field], 'Tümü')}
        {choices.map(([value, label]) => chip(value, label))}
      </div>
    );
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative min-w-0 flex-1 basis-48 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
            placeholder="Deneme ara"
            aria-label="Deneme ara"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <span className="whitespace-nowrap text-sm text-gray-500">{shownCount} / {exams.length} deneme</span>
        {active && (
          <button type="button" onClick={() => onChange(EMPTY_STUDENT_EXAM_FILTERS)} className="text-xs text-blue-600 hover:underline">
            Temizle
          </button>
        )}
      </div>
      {row('kind', 'Tür', options.kinds.map((kind) => [kind, kind]), examKind)}
      {row('progress', 'Durum', options.progresses, examProgress)}
      {row('grade', 'Sınıf', options.grades.map((grade) => [grade, `${grade}. sınıf`]), (exam) => Number(exam.gradeLevel) || null)}
      {row('format', 'Biçim', options.formats, examFormat)}
    </div>
  );
}
